import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  Target,
  FileText,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  Sparkles,
  BarChart3,
  Link2,
  AlertCircle,
  Plug,
  ExternalLink,
  RefreshCw,
  ShoppingCart,
  Loader2,
  ListTodo,
  CircleDot,
  Eye,
  CircleCheck,
  Zap,
} from 'lucide-react';
import { KpiCard } from '../../components/dashboard/KpiCard';
import { ScoreCard } from '../../components/dashboard/ScoreCard';
import { TopMoversWidget } from '../../components/dashboard/TopMoversWidget';
import { OpenIssuesWidget } from '../../components/dashboard/OpenIssuesWidget';
import { OrganicLandingPagesTable } from '../../components/dashboard/OrganicLandingPagesTable';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { api } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { useNavigate } from 'react-router-dom';
import { 
  AreaChart,
  Area,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';

// Connect Analytics Prompt Component
const ConnectAnalyticsPrompt = ({ service, onConnect }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
      <Plug className="w-8 h-8 text-slate-400" />
    </div>
    <h3 className="font-heading font-semibold text-lg mb-2">Connect to view Analytics</h3>
    <p className="text-sm text-slate-500 mb-4 max-w-xs">
      Connect your {service} account to see real performance data
    </p>
    <Button onClick={onConnect} className="gds-btn-primary">
      <Plug className="w-4 h-4 mr-2" />
      Connect {service}
    </Button>
  </div>
);

// Property selector prompt
const PropertySelector = ({ properties, onSelect, loading: selectLoading }) => (
  <div className="flex flex-col items-center justify-center py-8 text-center">
    <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mb-3">
      <BarChart3 className="w-6 h-6 text-indigo-600" />
    </div>
    <h3 className="font-heading font-semibold text-lg mb-2">Select a GA4 Property</h3>
    <p className="text-sm text-slate-500 mb-4 max-w-sm">
      Choose which website property to pull analytics data from
    </p>
    <div className="w-full max-w-md space-y-2">
      {properties.map((prop) => (
        <button
          key={prop.property_id}
          onClick={() => onSelect(prop.property_id)}
          disabled={selectLoading}
          className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all text-left"
        >
          <div>
            <p className="font-medium text-sm">{prop.display_name}</p>
            <p className="text-xs text-slate-500">{prop.account_name} &middot; ID: {prop.property_id}</p>
          </div>
          <ArrowUpRight className="w-4 h-4 text-slate-400" />
        </button>
      ))}
    </div>
  </div>
);

// Metric card for overview stats
const MetricCard = ({ label, value, change, format = 'number' }) => {
  const formatted = format === 'percent'
    ? `${typeof value === 'number' ? value.toFixed(1) : value}%`
    : format === 'duration'
      ? `${Math.floor((value || 0) / 60)}m ${Math.round((value || 0) % 60)}s`
      : typeof value === 'number'
        ? value.toLocaleString()
        : value || '—';

  const isPositive = change > 0;
  const isNeutral = change === 0 || change === undefined || change === null;
  const isBounceRate = label.toLowerCase().includes('bounce');
  // For bounce rate, decrease is good
  const isGood = isBounceRate ? !isPositive : isPositive;

  return (
    <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className="text-2xl font-bold font-heading">{formatted}</p>
      {!isNeutral && change !== undefined && (
        <p className={`text-xs mt-1 ${isGood ? 'text-emerald-600' : 'text-red-500'}`}>
          {isPositive ? '+' : ''}{change}% vs prev period
        </p>
      )}
    </div>
  );
};

// Empty State for Approvals
const EmptyApprovalsState = () => (
  <div className="flex flex-col items-center justify-center py-8 text-center">
    <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
      <CheckCircle2 className="w-6 h-6 text-emerald-600" />
    </div>
    <p className="text-sm text-slate-600">No pending approvals</p>
    <p className="text-xs text-slate-400 mt-1">You're all caught up!</p>
  </div>
);

export default function ClientDashboard() {
  const { user } = useAuth();
  const { selectedProjectId } = useProject();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [realData, setRealData] = useState(null);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [ga4Properties, setGa4Properties] = useState([]);
  const [needsPropertySelection, setNeedsPropertySelection] = useState(false);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [gscData, setGscData] = useState(null);
  const [gscError, setGscError] = useState(null);
  const [gscLoading, setGscLoading] = useState(false);
  const [baselineRefreshing, setBaselineRefreshing] = useState(false);
  const [gscSites, setGscSites] = useState([]);
  const [needsSiteSelection, setNeedsSiteSelection] = useState(false);
  const [ecommerceData, setEcommerceData] = useState(null);
  const [taskSummary, setTaskSummary] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const fetchOverview = async () => {
    setOverviewLoading(true);
    try {
      const res = await api.get('/dashboard/overview', {
        params: selectedProjectId ? { project_id: selectedProjectId } : {},
      });
      if (res?.data) setOverview(res.data);
    } catch (e) {
      // ignore — overview is best-effort
    } finally {
      setOverviewLoading(false);
    }
  };

  const fetchAnalyticsData = async (days = 30) => {
    setAnalyticsLoading(true);
    try {
      const dataRes = await api.get('/analytics/data', { params: { days } });
      setRealData(dataRes.data);
      setAnalyticsError(null);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (detail.includes('No GA4 property selected')) {
        try {
          const propsRes = await api.get('/analytics/properties');
          const props = propsRes.data?.properties || [];
          if (props.length === 1) {
            await handleSelectProperty(props[0].property_id);
          } else if (props.length > 1) {
            setGa4Properties(props);
            setNeedsPropertySelection(true);
          } else {
            setAnalyticsError('No GA4 properties found for this Google account.');
          }
        } catch (propErr) {
          setAnalyticsError('Could not load GA4 properties. Please reconnect Google Analytics.');
        }
      } else if (detail.includes('No GA4 access token')) {
        setAnalyticsError('Please reconnect Google Analytics to enable data access.');
      } else {
        setAnalyticsError(detail || 'Could not load analytics data.');
      }
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchGscData = async (days) => {
    setGscLoading(true);
    try {
      const gscRes = await api.get('/analytics/gsc/data', { params: { days } });
      setGscData(gscRes.data);
      setGscError(null);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (detail.includes('No GSC site selected')) {
        try {
          const sitesRes = await api.get('/analytics/gsc/sites');
          const sites = sitesRes.data?.sites || [];
          if (sites.length === 1) {
            await handleSelectSite(sites[0].site_url);
          } else if (sites.length > 1) {
            setGscSites(sites);
            setNeedsSiteSelection(true);
          } else {
            setGscError('No sites found in Search Console.');
          }
        } catch (siteErr) {
          setGscError('Could not load GSC sites.');
        }
      } else {
        setGscError(detail || 'Could not load Search Console data.');
      }
    } finally {
      setGscLoading(false);
    }
  };

  useEffect(() => {
    if (!integrations) return;
    if (integrations.ga4?.status === 'connected' && !needsPropertySelection) {
      fetchAnalyticsData(analyticsDays);
    }
    if (integrations.gsc?.status === 'connected' && !needsSiteSelection) {
      fetchGscData(analyticsDays);
    }
    if (integrations.ga4?.status === 'connected') {
      (async () => {
        try {
          const ecomRes = await api.get('/analytics/ecommerce', { params: { days: analyticsDays } });
          setEcommerceData(ecomRes.data);
        } catch (e) {
          setEcommerceData(null);
        }
      })();
    } else {
      setEcommerceData(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- orchestrated loads; handlers close over current analyticsDays
  }, [integrations, analyticsDays, needsPropertySelection, needsSiteSelection]);

  const fetchDashboardData = async () => {
    try {
      setOverviewLoading(true);
      const [integrationsRes, onboardingRes, approvalsRes, overviewRes, taskRes] = await Promise.all([
        api.get('/integrations/status'),
        api.get('/onboarding/status'),
        api.get('/approvals', { params: { status: 'pending' } }),
        api.get('/dashboard/overview', { params: selectedProjectId ? { project_id: selectedProjectId } : {} }).catch(() => null),
        api.get('/tasks/client-summary').catch(() => null),
      ]);

      setIntegrations(integrationsRes.data);
      setOnboardingStatus(onboardingRes.data);
      setApprovals(approvalsRes.data?.items || approvalsRes.data || []);
      if (overviewRes?.data) {
        setOverview(overviewRes.data);
      }
      if (taskRes?.data) {
        setTaskSummary(taskRes.data);
      }

      // Auto-refresh baseline when integrations are connected but baseline wasn't captured yet
      const pendingBaseline = onboardingRes.data?.baseline?.source === 'pending_integration';
      const ga4Connected = integrationsRes.data?.ga4?.status === 'connected';
      const gscConnected = integrationsRes.data?.gsc?.status === 'connected';
      if (pendingBaseline && (ga4Connected || gscConnected)) {
        try {
          const baselineRes = await api.post('/onboarding/refresh-baseline');
          setOnboardingStatus(prev => ({ ...prev, baseline: baselineRes.data.baseline }));
        } catch (e) {
          console.error('Auto-baseline refresh failed:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      setOverviewLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  const autoRefreshBaseline = async () => {
    try {
      const res = await api.post('/onboarding/refresh-baseline');
      setOnboardingStatus(prev => ({ ...prev, baseline: res.data.baseline }));
    } catch (e) {
      console.error('Auto-baseline refresh failed:', e);
    }
  };

  const handleSelectSite = async (siteUrl) => {
    try {
      await api.post('/analytics/gsc/select-site', { site_url: siteUrl });
      setNeedsSiteSelection(false);
      setGscSites([]);
      await fetchGscData(analyticsDays);
      fetchOverview();
      if (onboardingStatus?.baseline?.source === 'pending_integration') {
        autoRefreshBaseline();
      }
    } catch (e) {
      setGscError('Failed to load GSC data for selected site.');
    }
  };

  const handleSelectProperty = async (propertyId) => {
    setPropertyLoading(true);
    try {
      await api.post('/analytics/select-property', { property_id: propertyId });
      setNeedsPropertySelection(false);
      setGa4Properties([]);
      await fetchAnalyticsData(analyticsDays);
      fetchOverview();
      if (onboardingStatus?.baseline?.source === 'pending_integration') {
        autoRefreshBaseline();
      }
    } catch (e) {
      setAnalyticsError('Failed to load analytics data for selected property.');
    } finally {
      setPropertyLoading(false);
    }
  };

  const handleConnectGA4 = () => {
    navigate('/dashboard/integrations');
  };

  const isGA4Connected = integrations?.ga4?.status === 'connected';
  const isGSCConnected = integrations?.gsc?.status === 'connected';
  const isGBPConnected = integrations?.gbp?.status === 'connected';
  const anyIntegrationConnected = isGA4Connected || isGSCConnected || isGBPConnected;

  // Baseline data from onboarding (if completed)
  const baseline = onboardingStatus?.baseline;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-7 w-48 rounded-md bg-slate-200 dark:bg-slate-800 animate-pulse" />
            <div className="h-4 w-64 rounded-md bg-slate-100 dark:bg-slate-800/50 animate-pulse" />
          </div>
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-xl border border-slate-200 dark:border-slate-800 p-6 space-y-4">
            <div className="h-5 w-40 rounded bg-slate-200 dark:bg-slate-800 animate-pulse" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 space-y-2">
                  <div className="h-3 w-16 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                  <div className="h-7 w-12 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                </div>
              ))}
            </div>
            <div className="h-48 rounded-xl bg-slate-100 dark:bg-slate-800/30 animate-pulse" />
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-6 space-y-4">
            <div className="h-5 w-32 rounded bg-slate-200 dark:bg-slate-800 animate-pulse" />
            <div className="h-40 rounded-xl bg-slate-100 dark:bg-slate-800/30 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="client-dashboard">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {anyIntegrationConnected 
              ? "Here's your digital marketing performance"
              : "Connect your analytics to see performance data"
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          {approvals.length > 0 && (
            <Button className="gds-btn-primary" onClick={() => navigate('/dashboard/approvals')}>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Review {approvals.length} Approval{approvals.length > 1 ? 's' : ''}
            </Button>
          )}
        </div>
      </div>

      {/* Integration Status Banner */}
      {!anyIntegrationConnected && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-amber-900 dark:text-amber-100">Complete your setup</p>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Connect Google Analytics to view your real performance data
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  onClick={handleConnectGA4}
                >
                  <Plug className="w-4 h-4 mr-2" />
                  Connect Now
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* ── Unified Overview Band ─────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="col-span-2 md:col-span-1 lg:col-span-1">
          <ScoreCard
            score={overview?.health_score?.value}
            grade={overview?.health_score?.grade}
            delta={overview?.health_score?.delta_30d}
            loading={overviewLoading}
          />
        </div>
        <div className="col-span-1 lg:col-span-1">
          <KpiCard
            label="Organic Traffic"
            value={overview?.organic_traffic?.sessions}
            delta={overview?.organic_traffic?.delta_pct}
            icon={TrendingUp}
            iconColor="text-indigo-500"
            iconBg="bg-indigo-50 dark:bg-indigo-900/30"
            sparklineData={overview?.organic_traffic?.sparkline}
            sparklineColor="#6366f1"
            loading={overviewLoading}
            onClick={() => navigate('/dashboard')}
          />
        </div>
        <div className="col-span-1 lg:col-span-1">
          <KpiCard
            label="Ecommerce (conv. / revenue)"
            value={overview?.conversions != null ? overview.conversions.revenue : null}
            delta={overview?.conversions?.delta_pct}
            format="currency"
            icon={ShoppingCart}
            iconColor="text-emerald-500"
            iconBg="bg-emerald-50 dark:bg-emerald-900/30"
            loading={overviewLoading}
            onClick={() => navigate('/dashboard/integrations')}
          />
        </div>
        <div className="col-span-1 lg:col-span-1">
          <KpiCard
            label="Avg. Position"
            value={overview?.keywords?.avg_position}
            delta={overview?.keywords?.position_delta}
            icon={Target}
            iconColor="text-cyan-500"
            iconBg="bg-cyan-50 dark:bg-cyan-900/30"
            loading={overviewLoading}
            onClick={() => navigate('/dashboard/keywords')}
          />
        </div>
        <div className="col-span-1 lg:col-span-1">
          <KpiCard
            label="Open Issues"
            value={(overview?.open_issues?.critical || 0) + (overview?.open_issues?.high || 0)}
            icon={Zap}
            iconColor="text-amber-500"
            iconBg="bg-amber-50 dark:bg-amber-900/30"
            loading={overviewLoading}
            onClick={() => navigate('/dashboard/onpage')}
          />
        </div>
      </div>

      {/* ── Top Movers + Open Issues row ─────────────────────────────────── */}
      <div className="grid md:grid-cols-2 gap-6">
        <TopMoversWidget topMovers={overview?.top_movers} loading={overviewLoading} />
        <OpenIssuesWidget issues={overview?.open_issues} loading={overviewLoading} />
      </div>

      <div className="space-y-8">
      {/* Main Dashboard Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        
        {/* Traffic & Performance - Large Card */}
        <Card className="lg:col-span-2 gds-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-500" />
              Traffic & Performance
            </CardTitle>
            {isGA4Connected && realData && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardDescription>
                  Live data from Google Analytics 4 &middot; Last {realData.period?.days || 30} days
                </CardDescription>
                <div className="flex gap-1">
                  {[7, 14, 30, 90].map((d) => (
                    <Button
                      key={d}
                      variant={analyticsDays === d ? 'default' : 'ghost'}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setAnalyticsDays(d)}
                    >
                      {d}d
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {isGA4Connected && needsPropertySelection ? (
              <PropertySelector
                properties={ga4Properties}
                onSelect={handleSelectProperty}
                loading={propertyLoading}
              />
            ) : isGA4Connected && analyticsLoading ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mb-4" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Loading analytics data...</p>
                <p className="text-xs text-slate-400 mt-1">Connecting to Google Analytics</p>
              </div>
            ) : isGA4Connected && analyticsError ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-3">
                  <AlertCircle className="w-6 h-6 text-red-500" />
                </div>
                <p className="text-sm text-slate-600 mb-2">{analyticsError}</p>
                <Button variant="outline" size="sm" onClick={handleConnectGA4}>
                  <Plug className="w-4 h-4 mr-2" /> Reconnect
                </Button>
              </div>
            ) : isGA4Connected && realData ? (
              <div className="space-y-6">
                {/* Overview Metric Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard
                    label="Sessions"
                    value={realData.overview?.sessions}
                    change={realData.overview?.sessionsChange}
                  />
                  <MetricCard
                    label="Users"
                    value={realData.overview?.totalUsers}
                    change={realData.overview?.usersChange}
                  />
                  <MetricCard
                    label="Page Views"
                    value={realData.overview?.pageViews}
                    change={realData.overview?.pageViewsChange}
                  />
                  <MetricCard
                    label="Bounce Rate"
                    value={realData.overview?.bounceRate}
                    change={realData.overview?.bounceRateChange}
                    format="percent"
                  />
                </div>

                {/* Traffic Chart */}
                <div className="h-64">
                  {(!realData.trafficData || realData.trafficData.length === 0 || 
                    realData.trafficData.every(d => (d.sessions || 0) === 0 && (d.users || 0) === 0)) ? (
                    <div className="flex flex-col items-center justify-center h-full rounded-xl bg-slate-50 dark:bg-slate-800/30 border border-dashed border-slate-200 dark:border-slate-700">
                      <BarChart3 className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" />
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-400">No traffic data in this period</p>
                      <p className="text-xs text-slate-500 mt-1 max-w-xs text-center">
                        Try a different time range, or ensure your GA4 tracking code is installed and receiving data.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={() => fetchAnalyticsData(analyticsDays)}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                      </Button>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={realData.trafficData}>
                        <defs>
                          <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} tickLine={false} />
                        <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} domain={[0, 'auto']} />
                        <Tooltip
                          contentStyle={{
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            fontSize: '12px',
                          }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="sessions" 
                          stroke="#6366f1" 
                          fillOpacity={1} 
                          fill="url(#colorSessions)"
                          strokeWidth={2}
                          name="Sessions"
                        />
                        <Area 
                          type="monotone" 
                          dataKey="users" 
                          stroke="#06b6d4" 
                          fillOpacity={1} 
                          fill="url(#colorUsers)"
                          strokeWidth={2}
                          name="Users"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* Top Pages Table */}
                {realData.topPages && realData.topPages.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-600 mb-3">Top Pages</h4>
                    <div className="space-y-2">
                      {realData.topPages.slice(0, 5).map((page, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/30"
                        >
                          <div className="flex-1 min-w-0 mr-4">
                            <p className="text-sm font-medium truncate">
                              {page.pageTitle || page.pagePath}
                            </p>
                            <p className="text-xs text-slate-400 truncate">{page.pagePath}</p>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span>{page.pageViews?.toLocaleString()} views</span>
                            <span>{typeof page.bounceRate === 'number' ? page.bounceRate.toFixed(1) : page.bounceRate}% bounce</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <ConnectAnalyticsPrompt service="Google Analytics" onConnect={handleConnectGA4} />
            )}
          </CardContent>
        </Card>

        {/* SEO Performance Card */}
        <Card className="gds-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Target className="w-5 h-5 text-cyan-500" />
              SEO Performance
            </CardTitle>
            {isGSCConnected && gscData && (
              <CardDescription>
                Data from Google Search Console · Last {gscData.period?.days || 28} days
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {isGSCConnected && needsSiteSelection ? (
              <div className="space-y-3 py-4">
                <p className="text-sm text-center text-slate-600 mb-3">Select a site to pull data from</p>
                {gscSites.map((site) => (
                  <button
                    key={site.site_url}
                    onClick={() => handleSelectSite(site.site_url)}
                    className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition-all text-left"
                  >
                    <p className="text-sm font-medium truncate">{site.site_url}</p>
                    <ArrowUpRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            ) : isGSCConnected && gscLoading ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-500 mb-3" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Loading search data...</p>
                <p className="text-xs text-slate-400 mt-1">Connecting to Search Console</p>
              </div>
            ) : isGSCConnected && gscError ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <AlertCircle className="w-8 h-8 text-red-400 mb-2" />
                <p className="text-xs text-slate-500">{gscError}</p>
              </div>
            ) : isGSCConnected && gscData ? (
              <div className="space-y-4">
                <div 
                  className="text-center py-3 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-900/20 cursor-pointer transition-colors"
                  onClick={() => navigate('/dashboard/keywords')}
                >
                  <p className="text-4xl font-bold font-heading text-indigo-600">
                    {gscData.overview?.totalKeywords || 0}
                  </p>
                  <p className="text-sm text-slate-500">Keywords Ranking</p>
                  <p className="text-xs text-indigo-500 mt-1 opacity-0 hover:opacity-100 transition-opacity">View all keywords →</p>
                </div>
                <div className="space-y-1">
                  <div 
                    className="flex justify-between text-sm p-2.5 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 cursor-pointer transition-colors group"
                    onClick={() => navigate('/dashboard/keywords', { state: { filter: 'top10' } })}
                  >
                    <span className="text-slate-600 group-hover:text-emerald-700 transition-colors">Top 10 Rankings</span>
                    <span className="font-medium group-hover:text-emerald-700 transition-colors flex items-center gap-1">
                      {gscData.overview?.top10 || 0}
                      <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-emerald-500" />
                    </span>
                  </div>
                  <div 
                    className="flex justify-between text-sm p-2.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-colors group"
                    onClick={() => navigate('/dashboard/keywords', { state: { sort: 'clicks' } })}
                  >
                    <span className="text-slate-600 group-hover:text-blue-700 transition-colors">Clicks</span>
                    <span className="font-medium group-hover:text-blue-700 transition-colors flex items-center gap-1">
                      {gscData.overview?.totalClicks?.toLocaleString() || 0}
                      <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-blue-500" />
                    </span>
                  </div>
                  <div 
                    className="flex justify-between text-sm p-2.5 rounded-lg hover:bg-violet-50 dark:hover:bg-violet-900/20 cursor-pointer transition-colors group"
                    onClick={() => navigate('/dashboard/keywords', { state: { sort: 'impressions' } })}
                  >
                    <span className="text-slate-600 group-hover:text-violet-700 transition-colors">Impressions</span>
                    <span className="font-medium group-hover:text-violet-700 transition-colors flex items-center gap-1">
                      {gscData.overview?.totalImpressions?.toLocaleString() || 0}
                      <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-violet-500" />
                    </span>
                  </div>
                  <div 
                    className="flex justify-between text-sm p-2.5 rounded-lg hover:bg-cyan-50 dark:hover:bg-cyan-900/20 cursor-pointer transition-colors group"
                    onClick={() => navigate('/dashboard/keywords', { state: { sort: 'ctr' } })}
                  >
                    <span className="text-slate-600 group-hover:text-cyan-700 transition-colors">Avg. CTR</span>
                    <span className="font-medium group-hover:text-cyan-700 transition-colors flex items-center gap-1">
                      {gscData.overview?.avgCTR || 0}%
                      <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-cyan-500" />
                    </span>
                  </div>
                  <div 
                    className="flex justify-between text-sm p-2.5 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 cursor-pointer transition-colors group"
                    onClick={() => navigate('/dashboard/keywords', { state: { sort: 'position' } })}
                  >
                    <span className="text-slate-600 group-hover:text-amber-700 transition-colors">Avg. Position</span>
                    <span className="font-medium group-hover:text-amber-700 transition-colors flex items-center gap-1">
                      {gscData.overview?.avgPosition || 0}
                      <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-amber-500" />
                    </span>
                  </div>
                </div>
                {/* Top Keywords */}
                {gscData.topQueries && gscData.topQueries.length > 0 && (
                  <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
                    <p className="text-xs font-medium text-slate-500 mb-2">Top Keywords</p>
                    <div className="space-y-1">
                      {gscData.topQueries.slice(0, 5).map((q, i) => (
                        <div 
                          key={i} 
                          className="flex items-center justify-between text-xs p-2 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 cursor-pointer transition-colors group"
                          onClick={() => navigate('/dashboard/keywords', { state: { search: q.query } })}
                        >
                          <span className="truncate flex-1 mr-2 text-slate-700 dark:text-slate-300 group-hover:text-indigo-700 dark:group-hover:text-indigo-400 transition-colors">
                            {q.query}
                          </span>
                          <span className="text-slate-500 whitespace-nowrap flex items-center gap-1 group-hover:text-indigo-600 transition-colors">
                            pos {q.position} · {q.clicks} clicks
                            <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </span>
                        </div>
                      ))}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full mt-2 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                      onClick={() => navigate('/dashboard/keywords')}
                    >
                      View All Keywords
                      <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <ConnectAnalyticsPrompt service="Search Console" onConnect={handleConnectGA4} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ecommerce Metrics */}
      {ecommerceData?.has_ecommerce && (
        <Card className="gds-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-emerald-500" />
              Ecommerce Performance
            </CardTitle>
            <CardDescription>
              Revenue and transaction data from Google Analytics (last {ecommerceData.period?.days || 28} days)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-center">
                <p className="text-2xl font-bold font-heading text-emerald-700">
                  ${ecommerceData.metrics.revenue?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                </p>
                <p className="text-sm text-slate-500">Revenue</p>
              </div>
              <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-center">
                <p className="text-2xl font-bold font-heading text-blue-700">
                  {ecommerceData.metrics.purchases?.toLocaleString() || 0}
                </p>
                <p className="text-sm text-slate-500">Purchases</p>
              </div>
              <div className="p-4 rounded-xl bg-violet-50 dark:bg-violet-900/20 text-center">
                <p className="text-2xl font-bold font-heading text-violet-700">
                  {ecommerceData.metrics.transactions?.toLocaleString() || 0}
                </p>
                <p className="text-sm text-slate-500">Transactions</p>
              </div>
              <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-center">
                <p className="text-2xl font-bold font-heading text-amber-700">
                  ${ecommerceData.metrics.avg_revenue_per_user?.toFixed(2) || '0.00'}
                </p>
                <p className="text-sm text-slate-500">Avg Revenue/User</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between">
                <span className="text-sm text-slate-600">Add to Carts</span>
                <span className="font-semibold">{ecommerceData.metrics.add_to_carts?.toLocaleString() || 0}</span>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between">
                <span className="text-sm text-slate-600">Checkouts</span>
                <span className="font-semibold">{ecommerceData.metrics.checkouts?.toLocaleString() || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Organic Landing Pages (shown when GA4 is connected) */}
      {isGA4Connected && <OrganicLandingPagesTable projectId={selectedProjectId} />}

      {/* Baseline Metrics (if captured during onboarding) */}
      {baseline && (
        <Card className="gds-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-500" />
                  Your Baseline (Starting Point)
                </CardTitle>
                <CardDescription>
                  Captured on {new Date(baseline.captured_at).toLocaleDateString()} - This is what we're improving from
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={baselineRefreshing}
                onClick={async () => {
                  setBaselineRefreshing(true);
                  try {
                    const res = await api.post('/onboarding/refresh-baseline');
                    setOnboardingStatus(prev => ({ ...prev, baseline: res.data.baseline }));
                  } catch { /* ignore */ }
                  setBaselineRefreshing(false);
                }}
              >
                {baselineRefreshing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-center">
                <p className="text-2xl font-bold font-heading">{baseline.organic_traffic != null ? baseline.organic_traffic.toLocaleString() : '0'}</p>
                <p className="text-sm text-slate-500">Organic Traffic</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-center">
                <p className="text-2xl font-bold font-heading">{baseline.keyword_count != null ? baseline.keyword_count : '0'}</p>
                <p className="text-sm text-slate-500">Keywords Tracked</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-center">
                <p className="text-2xl font-bold font-heading">{baseline.backlink_count != null ? baseline.backlink_count : '0'}</p>
                <p className="text-sm text-slate-500">Backlinks</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-center">
                <p className="text-2xl font-bold font-heading">{baseline.technical_seo_score != null ? `${baseline.technical_seo_score}/100` : 'N/A'}</p>
                <p className="text-sm text-slate-500">Technical Score</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Task Progress */}
      {taskSummary && taskSummary.total > 0 && (
        <Card className="gds-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-indigo-500" />
              Project Tasks
            </CardTitle>
            <Badge variant="outline">{taskSummary.total} total</Badge>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Status breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: 'todo', label: 'To Do', icon: ListTodo, color: 'slate' },
                { key: 'in_progress', label: 'In Progress', icon: CircleDot, color: 'blue' },
                { key: 'review', label: 'In Review', icon: Eye, color: 'amber' },
                { key: 'done', label: 'Done', icon: CircleCheck, color: 'emerald' },
              ].map(({ key, label, icon: Icon, color }) => (
                <div
                  key={key}
                  className={`p-4 rounded-xl bg-${color}-50 dark:bg-${color}-900/20 text-center`}
                >
                  <Icon className={`w-5 h-5 mx-auto mb-1 text-${color}-600`} />
                  <p className="text-2xl font-bold font-heading">
                    {taskSummary.by_status?.[key] || 0}
                  </p>
                  <p className="text-xs text-slate-500">{label}</p>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            {taskSummary.total > 0 && (
              <div>
                <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                  <span>Overall Progress</span>
                  <span>{Math.round(((taskSummary.by_status?.done || 0) / taskSummary.total) * 100)}%</span>
                </div>
                <Progress value={((taskSummary.by_status?.done || 0) / taskSummary.total) * 100} className="h-2" />
              </div>
            )}

            {/* Recent tasks */}
            {taskSummary.recent && taskSummary.recent.length > 0 && (
              <div>
                <p className="text-sm font-medium text-slate-600 mb-2">Recent Tasks</p>
                <div className="space-y-2">
                  {taskSummary.recent.map((t) => (
                    <button
                      type="button"
                      key={t.task_id}
                      onClick={() => setSelectedTask(t)}
                      className="w-full flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/30 text-left transition hover:bg-slate-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:hover:bg-slate-800 dark:focus:ring-indigo-800"
                    >
                      <div className="min-w-0 flex-1 mr-3">
                        <p className="text-sm font-medium truncate">{t.title}</p>
                        <p className="text-xs text-slate-400 capitalize">
                          {t.task_type?.replaceAll('_', ' ') || 'Task'} · Click to view details
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          t.status === 'done' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' :
                          t.status === 'in_progress' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                          t.status === 'review' ? 'border-amber-300 text-amber-700 bg-amber-50' :
                          'border-slate-300 text-slate-600'
                        }
                      >
                        {t.status === 'in_progress' ? 'In Progress' :
                         t.status === 'todo' ? 'To Do' :
                         t.status === 'review' ? 'In Review' : 'Done'}
                      </Badge>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pending Approvals */}
      <Card className="gds-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Pending Approvals
          </CardTitle>
          {approvals.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/approvals')}>
              View All
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {approvals.length === 0 ? (
            <EmptyApprovalsState />
          ) : (
            <div className="space-y-3">
              {approvals.slice(0, 5).map((item, i) => (
                <motion.div
                  key={item.approval_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      item.item_type === 'content' ? 'bg-indigo-100 text-indigo-600' :
                      item.item_type === 'keyword' ? 'bg-cyan-100 text-cyan-600' :
                      'bg-violet-100 text-violet-600'
                    }`}>
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{item.title}</p>
                      <p className="text-xs text-slate-500">
                        {item.description?.slice(0, 50)}...
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="capitalize">
                      {item.item_type?.replace('_', ' ')}
                    </Badge>
                    <Button 
                      size="sm" 
                      className="gds-btn-primary"
                      onClick={() => navigate('/dashboard/approvals')}
                    >
                      Review
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card 
          className="gds-card-interactive cursor-pointer" 
          onClick={() => navigate('/dashboard/content')}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <FileText className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <p className="font-heading font-semibold">Content</p>
              <p className="text-sm text-slate-600">View & approve content</p>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="gds-card-interactive cursor-pointer" 
          onClick={() => navigate('/dashboard/approvals')}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="font-heading font-semibold">Approvals</p>
              <p className="text-sm text-slate-600">Review pending items</p>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="gds-card-interactive cursor-pointer" 
          onClick={() => navigate('/dashboard/reports')}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <p className="font-heading font-semibold">Reports</p>
              <p className="text-sm text-slate-600">View monthly reports</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={Boolean(selectedTask)} onOpenChange={(open) => !open && setSelectedTask(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedTask?.title}</DialogTitle>
            <DialogDescription>
              {selectedTask?.task_type?.replaceAll('_', ' ') || 'Project task'}
            </DialogDescription>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">
                  {selectedTask.status === 'in_progress' ? 'In Progress' :
                   selectedTask.status === 'todo' ? 'To Do' :
                   selectedTask.status === 'review' ? 'In Review' : 'Done'}
                </Badge>
                {selectedTask.due_date && (
                  <Badge variant="secondary">
                    Due {new Date(selectedTask.due_date).toLocaleDateString()}
                  </Badge>
                )}
              </div>

              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                  What this task is about
                </p>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700 whitespace-pre-line dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-200">
                  {selectedTask.description || 'No task description was provided.'}
                </div>
              </div>

              {selectedTask.metadata?.target_url && (
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                    Target URL
                  </p>
                  <a
                    href={selectedTask.metadata.target_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline"
                  >
                    {selectedTask.metadata.target_url}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
